import { useCallback, useMemo } from 'react';
import { TimeZone } from '@connect-x-app/shared-domain-models';
import { DATE_TIME_FORMATS, DateTime, formatUsingTimeZone, getNow, isValidDate } from '@connect-x-app/shared-utils';
import { formatInTimeZone } from 'date-fns-tz';

import { useSettings } from './useSettings';

export const useTimeZone = () => {
  const { settings } = useSettings();

  const timeZoneInfo = useMemo(() => {
    const localIanaTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return { localIanaTimeZone, timeZoneAbbreviation: formatInTimeZone(getNow(), localIanaTimeZone, 'z') };
  }, []);

  const getTimeZoneFormat = useCallback((): string => {
    return settings.preferences.timeZone === TimeZone.UTC ? TimeZone.UTC : timeZoneInfo.localIanaTimeZone;
  }, [settings.preferences.timeZone, timeZoneInfo.localIanaTimeZone]);

  const formatWithCurrentZone = useCallback(
    (date: DateTime, formatString: string): string => {
      if (!isValidDate(date) || !timeZoneInfo.localIanaTimeZone) {
        return '';
      }
      return formatUsingTimeZone(date, getTimeZoneFormat(), formatString);
    },
    [getTimeZoneFormat, timeZoneInfo.localIanaTimeZone],
  );

  const formatWithIanaZone = useCallback(
    (date: DateTime, ianaTimeZone: string, formatString: string): string => {
      if (!isValidDate(date)) {
        return '';
      }
      const timeZone = settings.preferences.timeZone === TimeZone.UTC ? TimeZone.UTC : ianaTimeZone;
      return formatUsingTimeZone(date, timeZone, formatString);
    },
    [settings.preferences.timeZone],
  );

  const formatTimeInTimeZone = useCallback(
    (date: DateTime): string => formatWithCurrentZone(date, DATE_TIME_FORMATS.TIME),
    [formatWithCurrentZone],
  );

  const formatDateInTimeZone = useCallback(
    (date: DateTime): string => formatWithCurrentZone(date, DATE_TIME_FORMATS.DATE_TIME_LONG_NO_ZONE_FORMAT),
    [formatWithCurrentZone],
  );

  const formatTimeInIanaTimeZone = useCallback(
    (date: DateTime, ianaTimeZone: string): string => formatWithIanaZone(date, ianaTimeZone, DATE_TIME_FORMATS.TIME),
    [formatWithIanaZone],
  );

  const formatShortDateInIanaTimeZone = useCallback(
    (date: DateTime, ianaTimeZone: string): string => formatWithIanaZone(date, ianaTimeZone, DATE_TIME_FORMATS.SHORT_DATE),
    [formatWithIanaZone],
  );

  const formatLongDateInIanaTimeZone = useCallback(
    (date: DateTime, ianaTimeZone: string): string => formatWithIanaZone(date, ianaTimeZone, DATE_TIME_FORMATS.DATE_LONG),
    [formatWithIanaZone],
  );

  return {
    formatDateInTimeZone,
    formatLongDateInIanaTimeZone,
    formatShortDateInIanaTimeZone,
    formatTimeInIanaTimeZone,
    formatTimeInTimeZone,
    timeZoneAbbreviation: timeZoneInfo.timeZoneAbbreviation,
  };
};
